import orderBy from 'lodash/orderBy';
import {
    ALL_TEACHERS,
    ALL_YEARS
} from '../../../components/report-filters/filter-constants';
import {
    ENDPOINT_PATH_ALL_TEACHERS,
    ENDPOINT_PATH_ALL_YEARS,
    ENDPOINT_PATH_PARTICIPATION
} from '../../../helpers/constants';
import DateParamHelper from '../../../helpers/date-param-helper';
import Resources from '../../../helpers/resources';
import errorActions from '../../app/errors/errors-actions';
import { BRONZE, GOLD, SILVER } from '../config/certificate-constants';
import studentClassesActions from './student-classes/student-classes-actions';
import {
    NEW_CERTIFICATES_BRONZE,
    NEW_CERTIFICATES_SILVER,
    NEW_CERTIFICATES_GOLD,
    NEW_CERTIFICATES_PLATINUM,
    NEW_CERTIFICATES_EMERALD,
    NEW_CERTIFICATES_DIAMOND,
    NEW_CERTIFICATES_EPIC,
    NEW_CERTIFICATES_LEGEND,
    PAST_CERTIFICATES_TOTAL,
    NEW_CERTIFICATES_TOTAL
} from './all-classes-constants';
import { localeCode } from '../../../helpers/locale-helpers';
import { getTotalCurriculumAndLivePoints } from '../helpers/utils';

export const PARTICIPATION_ALL_CLASSES_RETRIEVED =
    'PARTICIPATION_ALL_CLASSES_RETRIEVED';

function getParticipationData(
    selectedDate,
    selectedYearGroupId,
    selectedTeacherId
) {
    return (dispatch, getState) => {
        const state = getState();
        if (state.isAdmin === true) {
            getAdminData(
                dispatch,
                selectedDate,
                selectedYearGroupId,
                selectedTeacherId
            );
        } else {
            getTeacherData(dispatch, selectedDate);
        }
    };
}

function getByClassData(selectedDate) {
    return (dispatch) =>
        Resources.byClassReport
            .get(DateParamHelper.getDateTypePath(selectedDate))
            .withParams(DateParamHelper.getDateQueryParams(selectedDate))
            .send()
            .then((payload) => {
                if (payload.valid) {
                    dispatch({
                        type: PARTICIPATION_ALL_CLASSES_RETRIEVED,
                        payload: mapParticipationData(payload.data)
                    });
                } else {
                    dispatch(
                        errorActions.addError(
                            PARTICIPATION_ALL_CLASSES_RETRIEVED
                        )
                    );
                }
            });
}

function getTop10StudentsData(selectedDate) {
    return (dispatch) =>
        Resources.top10StudentsReport
            .get(DateParamHelper.getDateTypePath(selectedDate))
            .withParams(DateParamHelper.getDateQueryParams(selectedDate))
            .send()
            .then((payload) => {
                if (payload.valid) {
                    dispatch(
                        studentClassesActions.setStudentClasses(
                            payload.data.topStudentParticipations
                        )
                    );
                    dispatch({
                        type: PARTICIPATION_ALL_CLASSES_RETRIEVED,
                        payload: mapTop10StudentsData(payload.data)
                    });
                } else {
                    dispatch(
                        errorActions.addError(
                            PARTICIPATION_ALL_CLASSES_RETRIEVED
                        )
                    );
                }
            });
}

function getAdminData(
    dispatch,
    selectedDate,
    selectedYearGroupId,
    selectedTeacherId
) {
    let yearGroupId = ENDPOINT_PATH_ALL_YEARS;
    let teacherId = ENDPOINT_PATH_ALL_TEACHERS;
    let resource = 'yearGroupReport';

    if (selectedYearGroupId != null && selectedYearGroupId !== ALL_YEARS) {
        yearGroupId = selectedYearGroupId;
    }
    if (selectedTeacherId != null && selectedTeacherId !== ALL_TEACHERS) {
        teacherId = selectedTeacherId;
        resource = 'teacherReport';
    }

    const params = {
        reportType: ENDPOINT_PATH_PARTICIPATION,
        ...DateParamHelper.getDateTypePath(selectedDate),
        yearGroupId,
        teacherId
    };
    getData(resource, dispatch, selectedDate, params);
}

function getTeacherData(dispatch, selectedDate) {
    getData('allClassesReport', dispatch, selectedDate);
}

function getData(resource, dispatch, selectedDate, params = {}) {
    const locale = localeCode();
    params = {
        reportType: ENDPOINT_PATH_PARTICIPATION,
        ...DateParamHelper.getDateTypePath(selectedDate),
        ...params
    };
    Resources[resource]
        .get(params)
        .withParams({
            ...DateParamHelper.getDateQueryParams(selectedDate),
            locale
        })
        .send()
        .then((payload) => {
            if (payload.valid) {
                dispatch({
                    type: PARTICIPATION_ALL_CLASSES_RETRIEVED,
                    payload: mapParticipationData(payload.data)
                });
            } else {
                dispatch(
                    errorActions.addError(PARTICIPATION_ALL_CLASSES_RETRIEVED)
                );
            }
        });
}

// Helper function to map past certificates
function getPastCertificatesTableConfig({ bronze = 0, silver = 0, gold = 0 }) {
    return {
        [BRONZE.id]: bronze,
        [SILVER.id]: silver,
        [GOLD.id]: gold,
        [PAST_CERTIFICATES_TOTAL]: bronze + silver + gold
    };
}

// Helper function to map new certificates
function getNewCertificatesTableConfig({ newCertificate }) {
    const {
        bronze,
        silver,
        gold,
        platinum,
        emerald,
        diamond,
        epic,
        legend,
        total
    } = newCertificate;

    return {
        [NEW_CERTIFICATES_BRONZE]: bronze,
        [NEW_CERTIFICATES_SILVER]: silver,
        [NEW_CERTIFICATES_GOLD]: gold,
        [NEW_CERTIFICATES_PLATINUM]: platinum,
        [NEW_CERTIFICATES_EMERALD]: emerald,
        [NEW_CERTIFICATES_DIAMOND]: diamond,
        [NEW_CERTIFICATES_EPIC]: epic,
        [NEW_CERTIFICATES_LEGEND]: legend,
        [NEW_CERTIFICATES_TOTAL]: total
    };
}

function mapParticipationData(data) {
    const mappedParticipation = data.classroomParticipations.map((result) => {
        const pastCertificates = getPastCertificatesTableConfig(result);
        const newCertificates = getNewCertificatesTableConfig(result);

        return {
            ...result,
            id: result.classroomId,
            ...pastCertificates,
            ...newCertificates
        };
    });

    return {
        classroomParticipations: mappedParticipation,
        classroomUsage: mapUsage(data),
        certificates: mapCertificates(data),
        newCertificate: mapNewCertificates(data)
    };
}

function mapTop10StudentsData(data) {
    return {
        classroomParticipations: orderBy(
            data.topStudentParticipations.map((student) => {
                const pastCertificates = getPastCertificatesTableConfig(
                    student
                );
                const newCertificates = getNewCertificatesTableConfig(student);
                const mappedStudent = {
                    ...student,
                    id: student.userProfileId,
                    classroomName: `${student.firstName} ${student.lastName}`,
                    ...pastCertificates,
                    ...newCertificates
                };
                delete mappedStudent.userProfileId;
                delete mappedStudent.firstName;
                delete mappedStudent.lastName;
                delete mappedStudent.classroomId;
                return mappedStudent;
            }),
            ['totalPoints'],
            ['desc']
        ),
        classroomUsage: mapUsage({
            ...data,
            classroomParticipations: data.topStudentParticipations
        }),
        certificates: mapCertificates(data),
        newCertificate: mapNewCertificates(data)
    };
}

function mapUsage(data) {
    const defaultTimeOnline = { hours: 0, minutes: 0 };
    const {
        totalCurriculumPoints,
        totalLiveMathsPoints
    } = getTotalCurriculumAndLivePoints(data.classroomParticipations);
    return [
        {
            id: 0,
            totalSignIns: data.totalSignIns || 0,
            totalTimeOnline: data.totalTimeOnline || defaultTimeOnline,
            totalActivityPoints: data.totalActivityPoints || 0,
            totalLiveMathsPoints,
            totalCurriculumPoints,
            totalSkillQuestsPoints: data.totalSkillQuestsPoints || 0,
            totalActivityTimeOnline:
                data.totalActivityTimeOnline || defaultTimeOnline,
            totalSkillQuestsTimeOnline:
                data.totalSkillQuestsTimeOnline || defaultTimeOnline
        }
    ];
}

function mapCertificates(data) {
    const gold = data.totalGold || 0;
    const silver = data.totalSilver || 0;
    const bronze = data.totalBronze || 0;

    return {
        gold,
        silver,
        bronze,
        total: gold + silver + bronze
    };
}
function mapNewCertificates({
    newCertificate: {
        bronze = 0,
        diamond = 0,
        gold = 0,
        emerald = 0,
        epic = 0,
        legend = 0,
        platinum = 0,
        silver = 0,
        total = 0
    }
}) {
    return {
        gold,
        silver,
        bronze,
        diamond,
        emerald,
        epic,
        legend,
        platinum,
        total
    };
}

export default {
    getParticipationData,
    getByClassData,
    getTop10StudentsData
};



// WEBPACK FOOTER //
// ./containers/participation/all-classes/all-classes-actions.js